import { toast } from 'react-toastify';
import axios from 'axios';
import ENDPOINTS from '../config/endpoints';

const baseURL = ENDPOINTS.API_BASE || '/api';

const api = axios.create({
  baseURL,
  timeout: 60000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export function clearStoredAuthTokens() {
  localStorage.removeItem('token');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  try {
    const names = [
      'hx_at', 'hx_rt', 'hx_who',
      'ro_at', 'ro_rt', 'ro_who',
      'reachout_at', 'reachout_rt',
      'accessToken', 'refreshToken', 'JSESSIONID',
    ];
    const paths = ['/', '/api', '/api/', '/api/auth', '/api/auth/refresh'];
    names.forEach((name) => {
      paths.forEach((path) => {
        document.cookie = `${name}=; Max-Age=0; Path=${path}; SameSite=Lax`;
      });
    });
  } catch {
    // HttpOnly cookies are cleared by POST /auth/logout
  }
}

api.interceptors.request.use(
  (config) => {
    const headers = config.headers || {};
    if (typeof headers.set === 'function') {
      headers.set('Cache-Control', 'no-cache');
      headers.set('Pragma', 'no-cache');
    } else {
      config.headers = { ...headers, 'Cache-Control': 'no-cache', Pragma: 'no-cache' };
    }
    // Global default is application/json; that breaks multipart uploads unless removed here
    // so the browser can send multipart/form-data with a proper boundary.
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    if (token && !config.headers?.Authorization && !config.headers?.authorization) {
      config.headers = config.headers || {};
      if (typeof config.headers.set === 'function') config.headers.set('Authorization', `Bearer ${token}`);
      else config.headers.Authorization = `Bearer ${token}`;
    }
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const h = config.headers;
      if (h && typeof h.delete === 'function') {
        h.delete('Content-Type');
        h.delete('content-type');
      } else {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];
let refreshBlockedUntilMs = 0;

const SESSION_EXPIRED_MESSAGE = "Session expired. Please login again.";
const SESSION_ENDED_KEY = "reachout_session_ended";
const LOGGED_OUT_KEY = "reachout_logged_out";

function markSessionEndedForRedirect() {
  try {
    const now = String(Date.now());
    sessionStorage.setItem(SESSION_ENDED_KEY, now);
    localStorage.setItem(LOGGED_OUT_KEY, now);
    sessionStorage.removeItem("reachout_last_path");
    sessionStorage.removeItem("reachout_wizetok_bridge_attempted");
    sessionStorage.removeItem("reachout_cross_app_sso");
  } catch (e) {
    // best effort
  }
}

function setSessionExpiryNotice() {
  try {
    sessionStorage.setItem("auth_expired_reason", SESSION_EXPIRED_MESSAGE);
  } catch (e) {
    // best effort
  }
}

export function resolveApiErrorMessage(error, fallbackMessage = "Request failed. Please try again.") {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const code = error?.code;
  const rawMessage = String(error?.message || "");
  const normalized = rawMessage.toLowerCase();
  const url = String(error?.config?.url || "");
  const isLoginAttempt =
    url.includes("/auth/credentials-login") || url.includes("/auth/otp/");

  if (isLoginAttempt) {
    return data?.message || data?.detail || data?.error || fallbackMessage;
  }

  if (error?.isSessionExpired || status === 401) {
    return SESSION_EXPIRED_MESSAGE;
  }
  if (status === 408) {
    return data?.message || "Your session timed out. Please re-upload the resume.";
  }
  if (status === 504) {
    return data?.message || "The formatter timed out. Please retry.";
  }
  if (status === 413) {
    return data?.message || "Uploaded file is too large.";
  }
  if (code === "ECONNABORTED" || normalized.includes("timeout")) {
    return "Request timed out. Please retry.";
  }
  if (!error?.response && (normalized.includes("network error") || normalized.includes("failed to fetch"))) {
    return "Network issue detected. Check your connection and retry.";
  }

  return data?.message || data?.detail || data?.error || rawMessage || fallbackMessage;
}

function processQueue(error, token = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
}

function isPublicAuthUrl(url) {
  return (
    url.includes('/auth/credentials-login') ||
    url.includes('/auth/otp/') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/exchange-token') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/me') ||
    url.includes('/auth/select-tenant') ||
    url.includes('/auth/cross-app-handoff')
  );
}

let lastPermissionToast = { message: '', at: 0 };

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response && error.response.status;

    // Rules & Permissions refused the action: tell the user why (once per message).
    if (status === 403 && error.response.data && error.response.data.error === 'PERMISSION_DENIED') {
      error.permissionDenied = true;
      const message = error.response.data.message || 'You do not have permission to do that.';
      const now = Date.now();
      if (message !== lastPermissionToast.message || now - lastPermissionToast.at > 4000) {
        lastPermissionToast = { message, at: now };
        toast.warn(message, { toastId: `perm-${message}` });
      }
      return Promise.reject(error);
    }

    if (status !== 401) {
      return Promise.reject(error);
    }

    const url = String((originalRequest && originalRequest.url) || '');
    if (isPublicAuthUrl(url)) {
      return Promise.reject(error);
    }

    // If refresh already failed recently, do not spam refresh calls.
    if (Date.now() < refreshBlockedUntilMs) {
      clearStoredAuthTokens();
      setSessionExpiryNotice();
      markSessionEndedForRedirect();
      try {
        if (window.self === window.top) window.location.replace('/login');
      } catch (e) {}
      return Promise.reject(error);
    }

    if (!originalRequest || originalRequest._authRetry) {
      clearStoredAuthTokens();
      setSessionExpiryNotice();
      markSessionEndedForRedirect();
      try {
        if (window.self === window.top) window.location.replace('/login');
      } catch (e) {}
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          // Cookie-based auth: no Authorization header needed.
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._authRetry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post(
        `${baseURL}/auth/refresh`,
        {},
        { headers: { 'Content-Type': 'application/json' }, timeout: 60000, withCredentials: true }
      );
      // On cookie-based auth, backend rotates cookies; JSON tokens may be absent.
      void data;
      processQueue(null, 'cookie');
      isRefreshing = false;
      return api(originalRequest);
    } catch (refreshErr) {
      processQueue(refreshErr, null);
      isRefreshing = false;
      // Prevent tight refresh loops when the backend returns 400/401 for refresh.
      refreshBlockedUntilMs = Date.now() + 30_000;
      clearStoredAuthTokens();
      setSessionExpiryNotice();
      markSessionEndedForRedirect();
      try {
        if (window.self === window.top) window.location.replace('/login');
      } catch (e) {}
      const err = refreshErr instanceof Error ? refreshErr : new Error(SESSION_EXPIRED_MESSAGE);
      err.isSessionExpired = true;
      err.userMessage = SESSION_EXPIRED_MESSAGE;
      return Promise.reject(err);
    }
  }
);

export default api;
